import { useCallback, useMemo, useState } from 'react';

/**
 * Persisted, user-configurable column VISIBILITY + ORDER for a table, stored atomically in
 * localStorage so the two can never desync. This is a superset of usePersistentColumnVisibility
 * (which is visibility-only and shared with Analytics) — kept separate on purpose so extending
 * ordering here can't regress that hook or its tests.
 */
export interface ColumnConfigState {
  visibility: Record<string, boolean>;
  /** Column keys in left-to-right display order. */
  order: string[];
}

export interface UsePersistentColumnConfig {
  visibility: Record<string, boolean>;
  order: string[];
  /** order ∩ visible — the keys to actually render, in display order. Referentially stable
   *  between renders until visibility or order changes. */
  orderedVisibleKeys: string[];
  toggle: (key: string) => void;
  move: (key: string, direction: 'up' | 'down') => void;
  reorder: (fromIndex: number, toIndex: number) => void;
  reset: () => void;
}

export interface PersistentColumnConfigOptions {
  storageKey: string;
  /** All valid column keys (used to drop stale keys and detect newly-added ones). */
  allKeys: string[];
  defaultVisibility: Record<string, boolean>;
  /** Default left-to-right order; also the position new keys are appended at. */
  defaultOrder: string[];
  /** Optional legacy visibility-only key to migrate from once (e.g. the old show/hide-only store). */
  legacyVisibilityKey?: string;
}

/**
 * Reconcile a stored order against the current registry: keep known keys in their stored
 * position, drop keys no longer in the registry, dedupe, then append any registry keys missing
 * from the stored order at the end (in default-order sequence), so a column added in a later
 * release still shows up for a user who already saved a custom order.
 */
function reconcileOrder(stored: unknown, allKeys: string[], defaultOrder: string[]): string[] {
  const known = new Set(allKeys);
  const seen = new Set<string>();
  const result: string[] = [];
  if (Array.isArray(stored)) {
    for (const k of stored) {
      if (typeof k === 'string' && known.has(k) && !seen.has(k)) {
        result.push(k);
        seen.add(k);
      }
    }
  }
  for (const k of defaultOrder) {
    if (!seen.has(k)) {
      result.push(k);
      seen.add(k);
    }
  }
  return result;
}

function load(opts: PersistentColumnConfigOptions): ColumnConfigState {
  const { storageKey, allKeys, defaultVisibility, defaultOrder, legacyVisibilityKey } = opts;
  try {
    const raw = localStorage.getItem(storageKey);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<ColumnConfigState>;
      return {
        visibility: { ...defaultVisibility, ...(parsed.visibility ?? {}) },
        order: reconcileOrder(parsed.order, allKeys, defaultOrder),
      };
    }
    // One-time migration from a legacy visibility-only store so existing users keep their
    // show/hide choices; new keys (and the order) fall back to defaults.
    if (legacyVisibilityKey) {
      const legacyRaw = localStorage.getItem(legacyVisibilityKey);
      if (legacyRaw) {
        const legacy = JSON.parse(legacyRaw) as Record<string, boolean>;
        return {
          visibility: { ...defaultVisibility, ...legacy },
          order: [...defaultOrder],
        };
      }
    }
  } catch {
    /* corrupt JSON or storage unavailable — fall through to defaults */
  }
  return { visibility: { ...defaultVisibility }, order: [...defaultOrder] };
}

export function usePersistentColumnConfig(
  opts: PersistentColumnConfigOptions,
): UsePersistentColumnConfig {
  const { storageKey, defaultVisibility, defaultOrder } = opts;
  // Lazy init: read localStorage once on mount. `opts` is expected to be built from module-scoped
  // constants, so this deliberately does not re-run when its identity changes.
  const [state, setState] = useState<ColumnConfigState>(() => load(opts));

  const persist = useCallback(
    (next: ColumnConfigState) => {
      try {
        localStorage.setItem(storageKey, JSON.stringify(next));
      } catch {
        /* storage unavailable — keep in-memory state only */
      }
      return next;
    },
    [storageKey],
  );

  const toggle = useCallback(
    (key: string) => {
      setState((prev) =>
        persist({
          ...prev,
          visibility: { ...prev.visibility, [key]: !prev.visibility[key] },
        }),
      );
    },
    [persist],
  );

  const reorder = useCallback(
    (fromIndex: number, toIndex: number) => {
      setState((prev) => {
        if (
          fromIndex === toIndex ||
          fromIndex < 0 ||
          toIndex < 0 ||
          fromIndex >= prev.order.length ||
          toIndex >= prev.order.length
        ) {
          return prev;
        }
        const order = [...prev.order];
        const [moved] = order.splice(fromIndex, 1);
        order.splice(toIndex, 0, moved);
        return persist({ ...prev, order });
      });
    },
    [persist],
  );

  const move = useCallback(
    (key: string, direction: 'up' | 'down') => {
      setState((prev) => {
        const idx = prev.order.indexOf(key);
        if (idx < 0) return prev;
        const target = direction === 'up' ? idx - 1 : idx + 1;
        if (target < 0 || target >= prev.order.length) return prev;
        const order = [...prev.order];
        [order[idx], order[target]] = [order[target], order[idx]];
        return persist({ ...prev, order });
      });
    },
    [persist],
  );

  const reset = useCallback(() => {
    setState(persist({ visibility: { ...defaultVisibility }, order: [...defaultOrder] }));
  }, [persist, defaultVisibility, defaultOrder]);

  const orderedVisibleKeys = useMemo(
    () => state.order.filter((k) => state.visibility[k]),
    [state.order, state.visibility],
  );

  return {
    visibility: state.visibility,
    order: state.order,
    orderedVisibleKeys,
    toggle,
    move,
    reorder,
    reset,
  };
}

export default usePersistentColumnConfig;
